import { ColumnHelper } from "@tanstack/react-table";
import { AccessorFnColumnDef } from "@tanstack/react-table";
import { Table as TanstackTable } from "@tanstack/react-table";
import { FIELD_MARKED_FOR_REMOVAL_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import {
  ActivityFormType,
  MultiActivityForm,
  RolesFormKey
} from "PFApp/types/activity_form/activity_form_schema";
import Table from "PFComponents/table";
import { DragAxis, InteractiveTable } from "PFComponents/table";
import { Subtemplate } from "PFCore/types";
import { RefObject, useRef } from "react";
import { useWatch } from "react-hook-form";

import { useInteractiveFormHandlers } from "./hooks/use_interactive_form_handlers";
import { useRolesTableColumns } from "./hooks/use_roles_table_columns";
import css from "./tabular_roles_form.module.scss";

// TODO: [SP-7392] [not for MVP] Each role may have different columns visible due to creator permissions
type TabularRolesFormProps = {
  subtemplate: Subtemplate | null;
  rolesFormKey: RolesFormKey;
  columnsBlacklist?: string[];
  checkDisabled?: (role: ActivityFormType) => boolean;
  generateActionsColumn?: (columnHelper: ColumnHelper<any>) => AccessorFnColumnDef<any, any>;
  scrollableContainerRef?: RefObject<HTMLElement | null>;
  nonInteractiveLastColumn?: boolean;
  getFixedHeaderWrapperStyle?: () => Partial<CSSStyleDeclaration> | undefined;
  skipRequiredFieldsValidation?: boolean;
};

export const TabularRolesForm = ({
  subtemplate,
  rolesFormKey,
  columnsBlacklist,
  checkDisabled,
  generateActionsColumn,
  scrollableContainerRef,
  nonInteractiveLastColumn,
  getFixedHeaderWrapperStyle,
  skipRequiredFieldsValidation
}: TabularRolesFormProps) => {
  const tableInstanceRef = useRef<TanstackTable<any> | null>(null);

  const tableRowsInfo = useWatch<MultiActivityForm, RolesFormKey>({
    name: rolesFormKey,
    compute: (roles = []) =>
      roles.map((role) => ({
        uniqueId: role.uniqueId,
        disabled: !!role[FIELD_MARKED_FOR_REMOVAL_KEY] || (checkDisabled?.(role) ?? false)
      }))
  }) as { uniqueId: string; disabled: boolean }[];

  const { generateColumns, hiddenFormControllers, getFieldPath, getFieldByColumnId, getCellBehavior } =
    useRolesTableColumns({
      subtemplate,
      rolesFormKey,
      columnsBlacklist,
      generateActionsColumn,
      rolesCount: tableRowsInfo.length, // we need to refresh columns when roles where added/removed
      tableInstanceRef,
      skipRequiredFieldsValidation
    });

  const { handleFill, handlePaste, handleClear } = useInteractiveFormHandlers({
    tableInstanceRef,
    rolesFormKey,
    tableRowsInfo,
    getFieldPath,
    getFieldByColumnId,
    getCellBehavior
  });

  return (
    <>
      {hiddenFormControllers}
      <InteractiveTable
        dragAxis={DragAxis.Vertical}
        tableInstanceRef={tableInstanceRef}
        restrictPasteToSourceColumn
        onFill={handleFill}
        onPaste={handlePaste}
        onClear={handleClear}
        scrollableContainerRef={scrollableContainerRef}
        nonInteractiveLastColumn={nonInteractiveLastColumn}
      >
        <Table
          generateColumns={generateColumns}
          tableData={tableRowsInfo}
          className={css.table}
          enableSorting={false}
          tableInstanceRef={tableInstanceRef}
          scrollableContainerRef={scrollableContainerRef}
          hideDistantRows
          noHover
          displayGrid
          disableInternalScrolling
          disableAutoResizing
          isInteractive
          enableFixedHeader
          getFixedHeaderWrapperStyle={getFixedHeaderWrapperStyle}
        />
      </InteractiveTable>
    </>
  );
};
